<template>
  <OpportunityAreaLearnMore :isOpen="isLearnMoreOpen" @closeModal="closeLearnMore" />
  <OpportunityAreaChangeCommissionModal
    :isOpen="isChangeCommissionLevelModalOpen"
    :currentCpa="currentCommissionLevel"
    :newCpa="selectedCommissionLevel"
    :prevCpa="prevCommissionLevel"
    :language="language"
    :isLoading="isUpdating"
    :campaignStatus="currentCampaignStatus"
    :hasActivePartner="
      currentRCPartner !== null && currentRCPartner?.partnerStatus === 5 && currentRCPartner.connectionStatus === 5
    "
    :hasMorePartners="selectablePartners.length > 0"
    :currentStep="currentStep"
    @onChange="onCommissionChange"
    @onResume="onCampaignResume"
    @closeModal="closeChangeCommission"
  />
  <div class="tw:pb-8 tw:mb-8 tw:border-b tw:border-gray-300">
    <h1 class="tw:text-black tw:font-bold tw:text-2xl tw:mb-3">
      {{ $t('rc_opportunities_headline') }}
    </h1>

    <p>
      {{ $t('rc_opportunities_subtext') }}
      <button @click="openLearnMore" class="tw:text-blue-700 tw:underline">
        {{ $t('rc_opportunities_learn_more') }}
      </button>
    </p>
    <div class="tw:flex tw:flex-col tw:md:flex-row tw:mt-8 tw:gap-x-8">
      <div class="tw:w-full tw:md:w-1/2 tw:mb-8 tw:md:mb-0">
        <ul class="tw:gap-y-4 tw:flex tw:flex-col">
          <li v-for="item in commissions" :key="item.createdAt">
            <CommissionItem
              :cpa="item.newCpa"
              :variation="item.projectedShareVariation"
              :currentCommissionLevel="item.currentCpa"
              :selectedCommissionLevel="selectedCommissionLevel"
              @onChange="onChange"
              @onSelect="openChangeCommission"
            />
          </li>
        </ul>
      </div>

      <div class="tw:w-full tw:md:w-1/2">
        <CommissionLevelsGraphContainer
          :series="seriesData.data"
          :colors="seriesData.colors"
          :categories="categories"
          :selectedCommissionLevel="selectedCommissionLevel"
          :currentCommissionLevel="currentCommissionLevel"
          :highestPossibleUpgrade="highestPossibleUpgrade"
          @selectLevel="onChange"
        />
      </div>
    </div>
    <div class="tw:flex tw:flex-col tw:md:flex-row tw:mt-3 tw:gap-x-8">
      <div class="tw:w-1/2" />
      <div class="tw:md:w-1/2">
        <p class="tw:text-xs">{{ $t('rc_opportunities_top_position_footnote') }}</p>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, computed } from 'vue'

import CommissionItem from '@/components/rateConnect/opportunityArea/commissionLevel/CommissionItem.vue'
import CommissionLevelsGraphContainer from '@/components/rateConnect/opportunityArea/commissionLevel/CommissionLevelsGraphContainer.vue'
import OpportunityAreaLearnMore from '@/components/rateConnect/opportunityArea/commissionLevel/OpportunityAreaLearnMore.vue'
import OpportunityAreaChangeCommissionModal from '@/components/rateConnect/opportunityArea/commissionLevel/OpportunityAreaChangeCommissionModal.vue'

import useToastNotifications from '@/components/notifications/useToastNotifications'
import appAnalytics from '@/utils/tracking'

import { useExpectedTopShare, useCampaign, useCampaignMutation, usePartners } from '@/components/rateConnect/queries'
import { formatPercentage } from '@/utils/Utility.ts'
import { selectedAccommodationId } from '@/layouts/queries'
import { useRateConnect } from '@/components/rateConnect/useRateConnect'
import { useSessionValues } from '@/composables/useSessionValues'

const { displayNotification } = useToastNotifications()
const { language } = useSessionValues()
const isLearnMoreOpen = ref(false)
const isChangeCommissionLevelModalOpen = ref(false)
const prevCommissionLevel = ref(null)
const currentStep = ref(1)
const isUpdating = ref(false)

const { partnersData } = usePartners(selectedAccommodationId)
const { currentPartner } = useCampaign(selectedAccommodationId)
const currentRCPartner = computed(() => {
  const partner = partnersData.value?.find(item => {
    return item.partnerId === currentPartner.value
  })

  return partner ?? null
})
const selectablePartners = computed(() =>
  partnersData.value?.filter(
    item =>
      item.partnerId !== currentPartner.value &&
      item?.partnerStatus === 5 &&
      item.connectionStatus === 5 &&
      item.canNetCpa
  )
)

const { updateCommissionLevel } = useCampaignMutation()
const { currentCommissionLevel, campaignsData } = useCampaign(selectedAccommodationId)
const { toggleResumeCampaign } = useRateConnect()

const { expectedTopShare } = useExpectedTopShare(currentCommissionLevel)
const getCommissions = allCommissions => {
  if (!allCommissions) return

  const existingData = [...allCommissions]
  const current = {
    currentCpa: currentCommissionLevel.value,
    newCpa: currentCommissionLevel.value,
    projectedShareVariation: 0,
    createdAt: new Date(),
  }

  existingData?.push(current)
  return existingData?.sort((a, b) => (a.newCpa < b.newCpa ? -1 : b.newCpa < a.newCpa ? 1 : 0))
}

const commissions = computed(() => getCommissions(expectedTopShare.value))
const currentLevel = computed(
  () => (expectedTopShare?.value && expectedTopShare.value[0]?.currentCpa) || currentCommissionLevel.value
)

const currentCampaignStatus = computed(() => campaignsData.value?.status)
const selectedCommissionLevel = ref(currentLevel.value)

const highestPossibleUpgrade = computed(() => {
  return expectedTopShare.value?.find(item => item.newCpa === 25)?.projectedShareVariation
})
const openLearnMore = () => {
  isLearnMoreOpen.value = true
}
const closeLearnMore = () => {
  isLearnMoreOpen.value = false
}
const closeChangeCommission = () => {
  currentStep.value = 1
  isChangeCommissionLevelModalOpen.value = false
}
const openChangeCommission = () => {
  appAnalytics.track(appAnalytics.events.RC_OPPORTUNITY_SELECT_COMMISSION, {
    selectedCpa: selectedCommissionLevel.value,
    accommodationId: selectedAccommodationId.value,
  })
  isChangeCommissionLevelModalOpen.value = true
}

const onChange = newCpa => {
  selectedCommissionLevel.value = Number(newCpa)
}

const getFormattedPercentage = item => {
  let newItem = formatPercentage(language.value, item, 0, 0)
  if (language.value === 'de-DE' || language.value === 'es-ES' || language.value === 'fr-FE') {
    newItem = newItem.slice(0, -1) + ' %'
  }

  return newItem
}

const categories = computed(() => {
  return commissions.value?.map(item => getFormattedPercentage(item.newCpa))
})

const getBarColor = item => {
  if (item.newCpa === selectedCommissionLevel.value) {
    return '#0088D9'
  } else if (item.newCpa > currentLevel.value) {
    return '#8DE28F'
  } else if (item.newCpa < currentLevel.value) {
    return '#F5B1C1'
  } else if (item.newCpa === currentLevel.value && item.newCpa !== selectedCommissionLevel.value) {
    return '#8DE28F'
  }
}
const seriesData = computed(() => {
  const data = []
  const colors = []
  let numberValue
  commissions.value?.forEach(item => {
    colors.push(getBarColor(item))

    if (item.projectedShareVariation > 0) {
      numberValue = 1 + Math.abs(item.projectedShareVariation) / 100
      data.push(numberValue)
    }
    if (item.projectedShareVariation < 0) {
      numberValue = 1 - Math.abs(item.projectedShareVariation) / 100
      data.push(numberValue)
    }
    if (item.projectedShareVariation === 0) {
      data.push(1)
    }
  })

  return { data: [{ data: data }], colors }
})

const onCommissionChange = () => {
  isUpdating.value = true
  prevCommissionLevel.value = currentLevel.value
  updateCommissionLevel(
    {
      accommodationId: selectedAccommodationId.value,
      commissionLevel: selectedCommissionLevel.value,
    },
    {
      onSuccess: () => {
        isUpdating.value = false
        appAnalytics.track(appAnalytics.events.RC_OPPORTUNITY_CHANGE_COMMISSION, {
          selectedCpa: selectedCommissionLevel.value,
          accommodationId: selectedAccommodationId.value,
        })

        if (currentCampaignStatus.value === 'ACTIVE') {
          displayNotification({
            title: 'setting_updated_success',
            titleTranslationKey: true,
            message: 'rc_cpa_commission_change_success',
            isTranslationKey: true,
            type: currentCommissionLevel.value > selectedCommissionLevel.value ? 'info' : 'success',
          })
          closeChangeCommission()
        } else {
          currentStep.value = 2
        }
      },
      onError: () => {
        isUpdating.value = false
        displayNotification({
          message: 'something_went_wrong',
          isTranslationKey: true,
          type: 'error',
        })
        if (currentCampaignStatus.value === 'ACTIVE') {
          closeChangeCommission()
        }
      },
    }
  )
}

const onCampaignResume = () => {
  toggleResumeCampaign('open')
}
</script>
